/**
 * Route and location utilities for driver tracking
 */

interface LatLng {
  lat: number
  lng: number
}

interface Location {
  latitude: number
  longitude: number
}

/**
 * Convert Location to LatLng format
 */
export function locationToLatLng(location: Location): LatLng {
  return {
    lat: location.latitude,
    lng: location.longitude,
  }
}

/**
 * Calculate distance between two points in meters using Haversine formula
 */
export function calculateDistance(from: Location, to: Location): number {
  const R = 6371e3 // Earth's radius in meters
  const φ1 = (from.latitude * Math.PI) / 180
  const φ2 = (to.latitude * Math.PI) / 180
  const Δφ = ((to.latitude - from.latitude) * Math.PI) / 180
  const Δλ = ((to.longitude - from.longitude) * Math.PI) / 180

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * Find the closest point on a route segment to a given location
 */
function closestPointOnSegment(
  point: Location,
  segmentStart: Location,
  segmentEnd: Location,
): Location {
  const dx = segmentEnd.longitude - segmentStart.longitude
  const dy = segmentEnd.latitude - segmentStart.latitude

  if (dx === 0 && dy === 0) {
    return segmentStart
  }

  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.longitude - segmentStart.longitude) * dx +
        (point.latitude - segmentStart.latitude) * dy) /
        (dx * dx + dy * dy),
    ),
  )

  return {
    latitude: segmentStart.latitude + t * dy,
    longitude: segmentStart.longitude + t * dx,
  }
}

/**
 * Find the closest point on an entire route to a given location
 */
export function findClosestPointOnRoute(
  location: Location,
  routePoints: Location[],
): { point: Location; distance: number; segmentIndex: number } {
  let closestPoint = routePoints[0]
  let minDistance = calculateDistance(location, routePoints[0])
  let closestSegmentIndex = 0

  for (let i = 0; i < routePoints.length - 1; i++) {
    const segmentClosest = closestPointOnSegment(location, routePoints[i], routePoints[i + 1])
    const distance = calculateDistance(location, segmentClosest)

    if (distance < minDistance) {
      minDistance = distance
      closestPoint = segmentClosest
      closestSegmentIndex = i
    }
  }

  return {
    point: closestPoint,
    distance: minDistance,
    segmentIndex: closestSegmentIndex,
  }
}

/**
 * Snap driver location to route if needed based on distance rules
 */
export function snapLocationToRoute(
  driverLocation: Location,
  routePoints: Location[],
  pickupLocation: Location,
): Location {
  // Calculate distance from driver to pickup
  const distanceToPickup = calculateDistance(driverLocation, pickupLocation)

  // If within 500m of pickup, use raw GPS location
  if (distanceToPickup <= 500) {
    return driverLocation
  }

  // Otherwise, snap to closest point on route
  const { point } = findClosestPointOnRoute(driverLocation, routePoints)

  // If driver is more than 500m from route, might need recalculation
  // (handled elsewhere), but still return snapped location
  return point
}

/**
 * Check if route recalculation is needed
 */
export function shouldRecalculateRoute(
  driverLocation: Location,
  routePoints: Location[],
  lastRouteUpdate: Date,
): boolean {
  // Check if enough time has passed (120 seconds instead of 60)
  const timeSinceLastUpdate = Date.now() - lastRouteUpdate.getTime()
  if (timeSinceLastUpdate < 120000) {
    return false
  }

  // Check distance from route
  const { distance } = findClosestPointOnRoute(driverLocation, routePoints)

  // If more than 1000m from route (instead of 500m), recalculate
  return distance > 1000
}

/**
 * Decode Google's encoded polyline to array of coordinates
 */
export function decodePolyline(encoded: string): Location[] {
  const points: Location[] = []
  let index = 0
  let lat = 0
  let lng = 0

  while (index < encoded.length) {
    let shift = 0
    let result = 0
    let byte

    do {
      byte = encoded.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20)

    const dlat = result & 1 ? ~(result >> 1) : result >> 1
    lat += dlat

    shift = 0
    result = 0

    do {
      byte = encoded.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20)

    const dlng = result & 1 ? ~(result >> 1) : result >> 1
    lng += dlng

    points.push({
      latitude: lat / 1e5,
      longitude: lng / 1e5,
    })
  }

  return points
}
